/**
 * The floor and ceiling slabs in 3D. Both run over the room's OUTER
 * perimeter, under and over every wall, one wall thickness thick, with sides
 * facing out. Only the typed floor surfaces stop at the walls, leaving bare
 * slab beneath them and in a hollow wall's void.
 */
import { type FloorPlanRoom, type Point, roomFloorType, roomFreeElements, type Scene, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { FLOOR_PALETTE, PALETTE, rgbFromHex, type Selection } from '../editor-model';
import { triangulatePolygon } from '../math3d';
import { ALWAYS, type MeshBuilder, type RGB } from './mesh3d';
import { fillRings, type PlanToWorld, ringSides } from './sceneBox';

/** The floor slab's gray. */
const PLINTH_RGB: RGB = [0.30, 0.31, 0.33];

/** The base floor is the palette beige; each flooring type keeps its tint, as the 2D editor's floor colors do from index 1 on. */
function floorColor(shape: FloorPlanRoom, type: string): RGB {
  const base = roomFloorType(shape);
  if (type === base) return PALETTE.floor;
  const types = [...new Set(roomFreeElements(shape).filter(element => element.customAreaType === 'flooring').map(element => element.floorType || base))];
  return rgbFromHex(FLOOR_PALETTE[(types.indexOf(type) % (FLOOR_PALETTE.length - 1)) + 1]);
}

/** A slab ring's sides over [bottom, top], facing out, outlined along the bottom and top. The caller owns begin() and end(). */
function slabSides(mesh: MeshBuilder, ring: Point[], bottom: number, top: number, color: RGB, toWorld: PlanToWorld): void {
  for (const { start, end, facing } of ringSides(ring)) {
    const bottomStart = toWorld(start.x, start.y, bottom), bottomEnd = toWorld(end.x, end.y, bottom);
    const topStart = toWorld(start.x, start.y, top), topEnd = toWorld(end.x, end.y, top);
    mesh.quad(bottomStart, bottomEnd, topEnd, topStart, facing, color);
    mesh.line(bottomStart, bottomEnd);
    mesh.line(topStart, topEnd);
  }
}

/**
 * The floor: the typed room surfaces at height 0 over the inner perimeter,
 * the slab's own top where no room floor covers it (the band out to the outer
 * perimeter, showing under the walls and out through every doorway as the
 * threshold, and every hole a wall element leaves, so a hollow wall's void
 * shows bare slab), and the slab itself over [−floorDepth, 0], capped below
 * so the model reads solid from underneath. A selected room lights its whole
 * floor object in the accent, base surfaces and slab alike, since the slab is
 * the tap target that selects it; typed flooring areas keep their colors.
 */
export function buildFloor(
  mesh: MeshBuilder,
  shape: FloorPlanRoom,
  scene: Scene,
  outerRing: Point[],
  toWorld: PlanToWorld,
  floorDepth: number,
  selection?: Selection,
): void {
  const roomSelected = selection?.kind === 'room';
  const slabColor = roomSelected ? PALETTE.accent : PLINTH_RGB;
  const innerRings = scene.roomSolid.map(polygon => polygon[0]);
  const slabRing = outerRing.length ? outerRing : innerRings[0] ?? [];
  mesh.begin(ALWAYS);
  for (const floor of scene.floors) {
    const color = roomSelected && floor.type === roomFloorType(shape) ? PALETTE.accent : floorColor(shape, floor.type);
    for (const polygon of floor.polygons) fillRings(mesh, polygon, 0, color, toWorld);
  }
  if (outerRing.length) fillRings(mesh, [outerRing, ...innerRings], 0, slabColor, toWorld);
  for (const hole of scene.roomSolid.flatMap(polygon => polygon.slice(1))) fillRings(mesh, [hole], 0, slabColor, toWorld);
  if (slabRing.length >= 3) {
    mesh.solid({ layer: 'floor', rings: [slabRing], closed: true, base: -floorDepth, top: 0 });
    slabSides(mesh, slabRing, -floorDepth, 0, slabColor, toWorld);
    fillRings(mesh, [slabRing], -floorDepth, slabColor, toWorld, -1);
  }
  mesh.end();
}

/**
 * The ceiling slab, a mirror of the floor slab one wall thickness thick above
 * the ceiling. Its underside faces down, so the renderer culls it like a wall
 * in its own `group`: it shows only when that normal points out of the
 * screen. Over a floor-to-ceiling opening the underside is what shows above
 * the doorway.
 */
export function buildCeiling(mesh: MeshBuilder, scene: Scene, outerRing: Point[], toWorld: PlanToWorld, ceiling: number, group: number): void {
  const slabRing = outerRing.length ? outerRing : scene.roomSolid[0]?.[0] ?? [];
  if (slabRing.length < 3) return;
  const top = ceiling + WALL_THICKNESS;
  mesh.solid({ layer: 'ceiling', rings: [slabRing], closed: true, base: ceiling, top });
  mesh.begin(group);
  for (const triangle of triangulatePolygon([slabRing])) {
    mesh.fan(triangle.map(point => toWorld(point.x, point.y, ceiling)), [0, -1, 0], PALETTE.ceiling);
    mesh.fan(triangle.map(point => toWorld(point.x, point.y, top)), [0, 1, 0], PALETTE.ceiling);
  }
  slabSides(mesh, slabRing, ceiling, top, PALETTE.ceiling, toWorld);
  mesh.end();
}
